import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/common/text';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { InfoRow } from './InfoRow';
import {
  getCategoryName,
  getSubCategoryName,
  getEntryPermissionLabel,
  formatDateWithTime,
} from '@/utils/residents/maintenance';
import { useThemedStyles } from '@/hooks/residents/useThemeColors';

interface WorkRequestInfoSectionProps {
  ticket: IMaintenanceListResponse;
  variant?: 'mobile' | 'desktop';
}

export const WorkRequestInfoSection: React.FC<WorkRequestInfoSectionProps> = ({
  ticket,
  variant = 'mobile'
}) => {
  const styles = useWorkRequestInfoStyles();
  const category = getCategoryName(ticket);
  const subCategory = getSubCategoryName(ticket);
  const entryPermission = getEntryPermissionLabel(ticket);

  return (
    <View style={variant === 'mobile' ? styles.mobileSection : styles.desktopSection}>
      <Text style={variant === 'mobile' ? styles.mobileSectionTitle : styles.desktopSectionTitle}>
        Work request information
      </Text>

      <View style={styles.infoTable}>
        <InfoRow
          label="ID"
          value={String(ticket.maintenanceRequestId || '-')}
          variant={variant}
        />
        <InfoRow
          label="Submitted"
          value={formatDateWithTime(ticket.createdOn)}
          variant={variant}
        />
        <InfoRow
          label="Category"
          value={category}
          variant={variant}
        />
        <InfoRow
          label="Subcategory"
          value={subCategory}
          variant={variant}
        />
        <InfoRow
          label="Entry permission"
          value={entryPermission}
          variant={variant}
        />
        {ticket.updatedOn && (
          <InfoRow
            label="Last updated"
            value={formatDateWithTime(ticket.updatedOn)}
            variant={variant}
          />
        )}
        {ticket.completedOn && (
          <InfoRow
            label="Completed"
            value={formatDateWithTime(ticket.completedOn)}
            variant={variant}
          />
        )}
      </View>
    </View>
  );
};

const useWorkRequestInfoStyles = () =>
  useThemedStyles(({ colors }) => ({
    mobileSection: {
      gap: 16,
    },
    desktopSection: {
      paddingHorizontal: 24,
      paddingVertical: 24,
      gap: 16,
    },
    infoTable: {
      gap: 12,
    },
    mobileSectionTitle: {
      fontSize: 16,
      fontWeight: '600' as const,
      color: colors.text.primary,
      lineHeight: 24,
    },
    desktopSectionTitle: {
      fontSize: 16,
      fontWeight: '400' as const,
      color: colors.text.primary,
      lineHeight: 24,
    },
  }));
